import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Calculator, ArrowRight, CheckCircle2, DollarSign, FileText, Send } from "lucide-react";

const FEES_FINANCED = { dealer: 199, registration: 199, tag: 18, title: 20, mailing: 5 };
const FEES_CASH = { dealer: 199, registration: 0, tag: 0, title: 0, mailing: 0 };

function monthlyPayment(amountFinanced, apr, months) {
  if (!amountFinanced || !months) return 0;
  if (!apr) return amountFinanced / months;
  const r = apr / 100 / 12;
  return (amountFinanced * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
}

export default function EstimatePage() {
  const [params] = useSearchParams();
  const initialVehicleId = params.get("vehicle") || "";

  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState(initialVehicleId);
  const [salesPrice, setSalesPrice] = useState(50000);
  const [paymentType, setPaymentType] = useState("financed"); // cash | financed
  const [tradeIn, setTradeIn] = useState(0);
  const [downPayment, setDownPayment] = useState(5000);
  const [termMonths, setTermMonths] = useState(60);
  const [apr, setApr] = useState(8.99);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [contact, setContact] = useState({ full_name: "", email: "", phone: "" });

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      setVehicles(r.data || []);
      if (initialVehicleId) {
        const v = (r.data || []).find((x) => x.id === initialVehicleId);
        if (v) setSalesPrice(v.price);
      }
    });
  }, [initialVehicleId]);

  const onVehicle = (id) => {
    setVehicleId(id);
    const v = vehicles.find((x) => x.id === id);
    if (v) setSalesPrice(v.price);
  };

  const isCash = paymentType === "cash";
  const fees = isCash ? FEES_CASH : FEES_FINANCED;

  const calc = useMemo(() => {
    const price = Number(salesPrice) || 0;
    const tavt = Math.round((price + fees.dealer) * 0.07 * 100) / 100;
    const feeTotal = fees.dealer + fees.registration + fees.tag + fees.title + fees.mailing;
    const subtotal = price + feeTotal + tavt;
    const otd = Math.max(0, subtotal - Number(tradeIn) - Number(downPayment));
    const monthly = isCash ? 0 : monthlyPayment(otd, Number(apr), Number(termMonths));
    return { price, tavt, feeTotal, subtotal, otd, monthly };
  }, [salesPrice, tradeIn, downPayment, apr, termMonths, isCash, fees]);

  const veh = vehicles.find((v) => v.id === vehicleId);

  const sendEstimate = async (e) => {
    e.preventDefault();
    if (!contact.email || !contact.full_name) return toast.error("Name and email required");
    setSending(true);
    try {
      await api.post("/estimates", {
        full_name: contact.full_name,
        email: contact.email,
        phone: contact.phone,
        vehicle_id: vehicleId || null,
        sales_price: calc.price,
        payment_type: paymentType,
        trade_in: Number(tradeIn),
        down_payment: Number(downPayment),
        apr: Number(apr),
        term_months: Number(termMonths),
        dealer_fee: fees.dealer,
        registration_fee: fees.registration,
        tag_plate_fee: fees.tag,
        title_application_fee: fees.title,
        mailing_fee: fees.mailing,
        tavt: calc.tavt,
        total_otd: calc.otd,
        monthly_payment: calc.monthly,
      });
      setSent(true);
      toast.success("Estimate emailed to you!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <PublicShell>
      <section className="hero-bg pt-32 pb-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="brand-divider max-w-xs mb-4">
            <span className="dot" />
            <span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Quick estimate</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl">
            <span className="chrome-text">Out-the-door price.</span> <span className="gold-text">In 30 seconds.</span>
          </h1>
          <p className="text-slate-300 max-w-2xl mt-4">
            No login, no credit pull. Drop in a price, choose cash or financed, and see your real total — Georgia TAVT, dealer fee, DMV bundle, monthly payment. Email it to yourself for the showroom visit.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-5 gap-6">
          {/* ----- INPUTS ----- */}
          <div className="lg:col-span-3 surface p-6 lg:p-8 rounded-md space-y-6">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Vehicle (optional)</Label>
              <select
                value={vehicleId}
                onChange={(e) => onVehicle(e.target.value)}
                className="w-full bg-[#04060d] border border-white/10 h-11 mt-1 rounded-sm px-3 text-white text-sm"
                data-testid="estimate-vehicle"
              >
                <option value="">— Use a custom price below —</option>
                {vehicles.filter((v) => v.status === "available").map((v) => (
                  <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} — ${v.price?.toLocaleString()}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Vehicle Price</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="number" value={salesPrice}
                  onChange={(e) => setSalesPrice(e.target.value)}
                  className="bg-[#04060d] border-white/10 h-12 pl-9 font-mono text-lg"
                  data-testid="estimate-price"
                />
              </div>
            </div>

            {/* Payment type toggle */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Payment Type</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentType("cash")}
                  className={`p-4 rounded-sm text-left transition border ${isCash ? "border-emerald-400 bg-emerald-500/10" : "border-white/10 hover:border-white/30"}`}
                  data-testid="estimate-cash"
                >
                  <div className="font-heading text-base text-white mb-1">💵 Cash Purchase</div>
                  <div className="text-xs text-slate-400">No DMV fees from dealer. You handle title/tag.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentType("financed")}
                  className={`p-4 rounded-sm text-left transition border ${!isCash ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"}`}
                  data-testid="estimate-financed"
                >
                  <div className="font-heading text-base text-white mb-1">📝 Finance / Card</div>
                  <div className="text-xs text-slate-400">Includes full DMV bundle + monthly payment.</div>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <NumField label="Trade-in value" v={tradeIn} on={setTradeIn} testid="estimate-trade" />
              <NumField label="Down payment" v={downPayment} on={setDownPayment} testid="estimate-down" />
              {!isCash && (
                <>
                  <NumField label="APR (%)" v={apr} on={setApr} step={0.01} testid="estimate-apr" />
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Term (months)</Label>
                    <select
                      value={termMonths}
                      onChange={(e) => setTermMonths(Number(e.target.value))}
                      className="w-full bg-[#04060d] border border-white/10 h-11 mt-1 rounded-sm px-3 text-white"
                      data-testid="estimate-term"
                    >
                      {[36, 48, 60, 72, 84].map((m) => <option key={m} value={m}>{m} months</option>)}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ----- BREAKDOWN ----- */}
          <div className="lg:col-span-2">
            <div className="surface p-6 lg:p-7 rounded-md sticky top-24">
              <div className="flex items-center gap-2 mb-5">
                <Calculator className="w-5 h-5 text-[#D4AF37]" />
                <div className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Your estimate</div>
              </div>

              <Row label="Vehicle price" value={calc.price} />
              <Row label="Dealer processing fee" value={fees.dealer} />
              {!isCash && (
                <>
                  <Row label="Registration fee" value={fees.registration} />
                  <Row label="Tag & plate fee" value={fees.tag} />
                  <Row label="Title application fee" value={fees.title} />
                  <Row label="Mailing fee" value={fees.mailing} />
                </>
              )}
              <Row label="TAVT (7%)" value={calc.tavt} />
              {Number(tradeIn) > 0 && <Row label="Trade-in credit" value={-Number(tradeIn)} neg />}
              {Number(downPayment) > 0 && <Row label="Down payment" value={-Number(downPayment)} neg />}

              <div className="border-t border-white/10 mt-3 pt-3">
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-sm text-slate-300">Out-the-door total</span>
                  <span className="font-display text-2xl gold-text" data-testid="estimate-otd">${calc.otd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                {!isCash && (
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-slate-400">Est. monthly payment</span>
                    <span className="font-heading text-lg text-white" data-testid="estimate-monthly">${calc.monthly.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</span>
                  </div>
                )}
              </div>

              <p className="text-[10px] text-slate-500 mt-4 leading-relaxed">
                Estimate uses 7% Georgia TAVT and standard Wakzy fees. Final figures subject to credit approval, taxes at time of titling, and any add-ons.
              </p>
            </div>
          </div>
        </div>

        {/* ----- EMAIL CAPTURE ----- */}
        <div className="mt-8 gradient-border" style={{ borderRadius: "16px" }}>
          <div className="gradient-border-inner p-7 lg:p-9" style={{ borderRadius: "15px" }}>
            {sent ? (
              <div className="flex items-center gap-4" data-testid="estimate-sent">
                <CheckCircle2 className="w-10 h-10 text-emerald-400 flex-shrink-0" />
                <div>
                  <div className="font-heading text-xl chrome-text inline-block">Sent to {contact.email}</div>
                  <p className="text-slate-300 text-sm mt-1">Check your inbox for your Wakzy estimate. See you on the lot.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={sendEstimate} className="grid md:grid-cols-4 gap-3 items-end" data-testid="estimate-email-form">
                <div className="md:col-span-3 grid md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Your name</Label>
                    <Input value={contact.full_name} onChange={(e) => setContact({ ...contact, full_name: e.target.value })} required className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="estimate-name" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Email *</Label>
                    <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} required className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="estimate-email" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-400">Phone</Label>
                    <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="estimate-phone" />
                  </div>
                </div>
                <Button type="submit" disabled={sending} className="btn-cta rounded-sm h-11" data-testid="estimate-send-btn">
                  {sending ? "Sending…" : <><Send className="w-4 h-4 mr-2" /> Email Estimate</>}
                </Button>
              </form>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={vehicleId ? `/financing?vehicle=${vehicleId}` : "/financing"}>
            <Button variant="outline" className="rounded-sm border-[#D4AF37] text-[#D4AF37] bg-transparent hover:bg-[#D4AF37]/10" data-testid="estimate-to-financing">
              <FileText className="w-4 h-4 mr-2" /> Get pre-approved
            </Button>
          </Link>
          {veh && (
            <Link to={`/inventory/${veh.id}`}>
              <Button variant="ghost" className="text-slate-300" data-testid="estimate-to-vehicle">
                View {veh.year} {veh.make} {veh.model} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          )}
        </div>
      </section>
    </PublicShell>
  );
}

function Row({ label, value, neg }) {
  return (
    <div className="flex justify-between text-sm py-1.5">
      <span className="text-slate-400">{label}</span>
      <span className={`font-mono ${neg ? "text-emerald-400" : "text-slate-200"}`}>
        {neg ? "−" : ""}${Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </span>
    </div>
  );
}

function NumField({ label, v, on, step = 1, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-400">{label}</Label>
      <Input type="number" step={step} value={v} onChange={(e) => on(e.target.value)} className="bg-[#04060d] border-white/10 h-11 mt-1 font-mono" data-testid={testid} />
    </div>
  );
}
